import {
  AccountFeatureFlag,
  FeatureFlagValue,
  LegacyFeatureFlagValue,
  getAccountFeatureFlag,
} from '../FeatureFlagService/FeatureFlagService';

import { authorize as baseAuthorize } from './api';

let memoizedAccessToken: Promise<string> | null = null;
let tokenExpiration: number | null = null;
let isSettled = false;

export const isTokenExpired = () => {
  return Date.now() > tokenExpiration!;
};

export const createGetAccessToken = (authorize: typeof baseAuthorize) => {
  const tokenIsNotExpired = tokenExpiration && !isTokenExpired();
  if (memoizedAccessToken && (tokenIsNotExpired || !isSettled)) {
    return memoizedAccessToken;
  }

  isSettled = false;
  memoizedAccessToken = authorize()
    .then(({ accessToken, expiresIn }) => {
      tokenExpiration = Date.now() + expiresIn * 1000 * 0.75;
      return accessToken;
    })
    .finally(() => {
      isSettled = true;
    });

  return memoizedAccessToken;
};

export const getAccessToken = (authorize = baseAuthorize) => createGetAccessToken(authorize);

export enum User {
  AccountId = 'accountId',
  AvatarUrl = 'avatarUrl',
  EmployeeId = 'employeeId',
  FeatureFlags = 'featureFlags',
  CanAccessAutomaticReminder = 'canAccessAutomaticReminder',
  CanAccessInAppSalesChat = 'canAccessInAppSalesChat',
  CanAccessInAppSupportChat = 'canAccessInAppSupportChat',
  CanAccessTimeTracking = 'canAccessTimeTracking',
  CanAccessPricelist = 'canAccessPricelist',
  HasLimitedCrmAccess = 'hasLimitedCrmAccess',
  HasLimitedDealsAccess = 'hasLimitedDealsAccess',
  Uuid = 'uuid',
  Features = 'features',
  Permissions = 'permissions',
  FeatureFlagsLegacy = 'featureFlagsLegacy',
  DefaultCountry = 'defaultCountry',
  PopularCountries = 'popularCountries',
  DepartmentCountries = 'departmentCountries',
  IsAdministrator = 'isAdministrator',
  IsAllowedToExportExcel = 'isAllowedToExportExcel',
  ExternalFilePlatformType = 'externalFilePlatformType',
  Preferences = 'preferences',
  Email = 'email',
  FirstName = 'firstName',
  LastName = 'lastName',
  AccessibleModules = 'accessibleModules',
}

export enum Permission {
  Admin = 'admin',
  AlternativeOutgoingEmailAddresses = 'alternative_outgoing_email_addresses',
  BackOffice = 'back_office',
  Billing = 'billing',
  Calendar = 'calendar',
  Companies = 'companies',
  ConsumeCustomerWalletCredit = 'consume_customer_wallet_credit',
  Contacts = 'contacts',
  CostsOnProjects = 'costs_on_projects',
  CreditNotes = 'credit_notes',
  Dashboard = 'dashboard',
  Deals = 'deals',
  DeliveryNotes = 'delivery_notes',
  DepartmentEmails = 'company_entity_emails',
  Documents = 'documents',
  Events = 'events',
  FinancialsOnProjects = 'financials_on_projects',
  Insights = 'insights',
  Invoices = 'invoices',
  MarginOnQuotations = 'margin_on_quotations',
  MultipleDealPipelines = 'multiple_deal_pipelines',
  UnlimitedDealPipelines = 'unlimited_deal_pipelines',
  NextgenProjects = 'nextgen_projects',
  NextgenProjectsPreview = 'nextgen_projects_preview',
  OrderConfirmations = 'order_confirmations',
  Orders = 'orders',
  PrepaidBilling = 'prepaid_billing',
  ProductPurchasePrice = 'product_purchase_price',
  Products = 'products',
  ProjectPlanning = 'project_planning',
  Projects = 'projects',
  ProjectsFullAdminAccess = 'projects_full_admin_access',
  QuotationAsDedicatedModule = 'quotation_as_dedicated_module',
  QuotationExpiry = 'quotation_expiry',
  Reseller = 'reseller',
  ResourcePlanning = 'resource_planning',
  Settings = 'settings',
  Subscriptions = 'subscriptions',
  Targets = 'targets',
  Tickets = 'tickets',
  TimeTracking = 'time_tracking',
  Todos = 'todos',
  Trendstop = 'trendstop',
  Users = 'users',
  Webhooks = 'webhooks',
  WorkOrders = 'work_orders',
}

export enum Features {
  PublicModule = 'PUBLICMODULE',
  Dashboard = 'DASHBOARD',
  Contacts = 'CONTACTS',
  Companies = 'COMPANIES',
  Deals = 'DEALS',
  DocumentLayoutVariations = 'DOCUMENT_LAYOUT_VARIATIONS',
  Planning = 'PLANNING',
  Hr = 'HR',
  Mailing = 'MAILING',
  Projects = 'PROJECTS',
  Invoices = 'INVOICES',
  StockManagement = 'STOCK_MANAGEMENT',
  Subscriptions = 'SUBSCRIPTIONS',
  Training = 'TRAINING',
  TeamleaderAdmin = 'TEAMLEADER_ADMIN',
  Settings = 'SETTINGS',
  CronJobs = 'CRON_JOBS',
  Stats = 'STATS',
  Events = 'EVENTS',
  Products = 'PRODUCTS',
  Documents = 'DOCUMENTS',
  Reseller = 'RESELLER',
  ProjectPlanning = 'PROJECTPLANNING',
  Orders = 'ORDERS',
  DeliveryNotes = 'DELIVERY_NOTES',
  Tickets = 'TICKETS',
  OrderConfirmations = 'ORDERCONFIRMATIONS',
  Targets = 'TARGETS',
  ApiAndWebhooks = 'API_AND_WEBHOOKS',
  WorkOrders = 'WORK_ORDERS',
  DealAutomation = 'DEAL_AUTOMATION',
  ReminderAutomation = 'REMINDER_AUTOMATION',
  Teams = 'TEAMS',
  Departments = 'DEPARTMENTS',
  PriceLists = 'PRICE_LISTS',
  LimitedAccess = 'LIMITED_ACCESS',
  AdvancedHr = 'ADVANCED_HR',
  ProjectCloud = 'PROJECT_CLOUD',
  PurchasePrices = 'PURCHASE_PRICES',
  TimeTracking = 'TIME_TRACKING',
  InAppSupportChat = 'IN_APP_SUPPORT_CHAT',
  FinancingOpportunities = 'FINANCING_OPPORTUNITIES',
  DepartmentEmails = 'DEPARTMENT_EMAILS',
  AlternativeOutgoingEmailAddresses = 'ALTERNATIVE_OUTGOING_EMAIL_ADDRESSES',
  FinancialForecast = 'FINANCIAL_FORECAST',
  ProjectTimeline = 'PROJECT_TIMELINE',
  MultipleQuotations = 'MULTIPLE_QUOTATIONS',
  WeeklyTimeTracking = 'WEEKLY_TIME_TRACKING',
  MarginOnQuotations = 'MARGIN_ON_QUOTATIONS',
  QuotationExpiry = 'QUOTATION_EXPIRY',
  EnforceMultiFactorAuthentication = 'ENFORCE_MULTI_FACTOR_AUTHENTICATION',
  NextgenProjects = 'NEXTGEN_PROJECTS',
}

export enum Module {
  Dashboard = 'DASHBOARD',
  Mailing = 'MAILING',
  Invoices = 'INVOICES',
  Deals = 'DEALS',
  Tickets = 'TICKETS',
  Projects = 'PROJECTS',
  Contacts = 'CONTACTS',
  Companies = 'COMPANIES',
  Settings = 'SETTINGS',
  Products = 'PRODUCTS',
  Planning = 'PLANNING',
  Subscriptions = 'SUBSCRIPTIONS',
  WorkOrders = 'WORK_ORDERS',
  TimeTracking = 'TIME_TRACKING',
}

export interface UserInitialState {
  [User.AccountId]: number;
  [User.AvatarUrl]: string | null;
  [User.EmployeeId]: number;
  [User.FeatureFlags]: Record<string, FeatureFlagValue>;
  [User.CanAccessAutomaticReminder]: boolean;
  [User.CanAccessInAppSalesChat]: boolean;
  [User.CanAccessInAppSupportChat]: boolean;
  [User.CanAccessTimeTracking]: boolean;
  [User.CanAccessPricelist]: boolean;
  [User.HasLimitedCrmAccess]: boolean;
  [User.HasLimitedDealsAccess]: boolean;
  [User.Uuid]: string;
  [User.Features]: Features[];
  [User.Permissions]: Permission[];
  [User.FeatureFlagsLegacy]: Record<string, LegacyFeatureFlagValue>;
  [User.DefaultCountry]: string;
  [User.PopularCountries]: string[];
  [User.DepartmentCountries]: string[];
  [User.IsAdministrator]: boolean;
  [User.IsAllowedToExportExcel]: boolean;
  [User.ExternalFilePlatformType]: 'dropbox' | 'google_drive';
  [User.Email]: string;
  [User.FirstName]: string;
  [User.LastName]: string;
  [User.Preferences]: {
    language: string;
    timeZone: string;
    dateFormat: {
      short: string;
      medium: string;
      long: string;
    };
    numberFormat: {
      thousandsSeparator: string;
      decimalSeparator: string;
    };
    webApp: Record<string, any>;
  };
  [User.AccessibleModules]: Module[];
}

export function getAuthenticatedUser<K extends User>(key: K): UserInitialState[K];
export function getAuthenticatedUser(): UserInitialState;
export function getAuthenticatedUser(key?: User) {
  if (key) {
    return window.top!.__INITIAL_STATE__.user[key];
  }
  return window.top!.__INITIAL_STATE__.user;
}

export const hasModule = (module: Module) =>
  window.top!.__INITIAL_STATE__.user.accessibleModules.includes(module.toUpperCase() as Module);

export const hasLimitedCrmAccess = () => getAuthenticatedUser(User.HasLimitedCrmAccess);

export const hasLimitedDealsAccess = () => getAuthenticatedUser(User.HasLimitedDealsAccess);

export const canAccessTimeTracking = () => getAuthenticatedUser(User.CanAccessTimeTracking);

export const canAccessAutomaticReminder = () => getAuthenticatedUser(User.CanAccessAutomaticReminder);

export const canAccessPricelist = () => getAuthenticatedUser(User.CanAccessPricelist);

export const canAccessDedicatedQuotationModule = () =>
  getAuthenticatedUser(User.Permissions).includes(Permission.QuotationAsDedicatedModule);

export const canAccessInvoices = () => getAuthenticatedUser(User.Permissions).includes(Permission.Invoices);

export const canAccessProjects = () => getAuthenticatedUser(User.Permissions).includes(Permission.NextgenProjects);

export const canAccessProjectsPreview = () =>
  getAuthenticatedUser(User.Permissions).includes(Permission.NextgenProjectsPreview);

export const canAccessNextGenProjects = () =>
  canAccessProjects() && getAccountFeatureFlag(AccountFeatureFlag.NextGenProjectsIntegration);

export const canAccessCostsOnProjects = () =>
  getAuthenticatedUser(User.Permissions).includes(Permission.CostsOnProjects);

export const canAccessFinancialsOnProjects = () =>
  getAuthenticatedUser(User.Permissions).includes(Permission.FinancialsOnProjects);

export const canAccessProducts = () => getAuthenticatedUser(User.Permissions).includes(Permission.Products);

export const canAccessDashboard = () => getAuthenticatedUser(User.Permissions).includes(Permission.Dashboard);

export const canAccessPlanning = () => getAccountFeatureFlag(AccountFeatureFlag.ResourcePlanning);

export const canAccessContacts = () => getAuthenticatedUser(User.Permissions).includes(Permission.Contacts);

export const canAccessCompanies = () => getAuthenticatedUser(User.Permissions).includes(Permission.Companies);
